import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, Divider, IconButton } from "@mzc-pdc/ui";
import compareVersions, {
    VERSION_CM_12197_COLLECTION_SHARE_LINK,
    VERSION_CM_12198_COLLECTION_DOWNLOAD,
    VERSION_CM_12290_COLLECTION_ARCHIVE,
    VERSION_CM_12923_LIST_SEARCHBAR_FILTER,
    VERSION_CM_9766_COLLECTIONS,
} from "@cores/version";
import VersionedComponent from "@components/VersionedComponent";
import BreadcrumbsWithRouterPath from "../../containers/BreadcrumbsWithRouterPath/BreadcrumbsWithRouterPath";
import { IconArrowDropDown, IconArrowDropUp, IconBack } from "@mzc-cloudplex/icons";
import { PageHeader } from "@features/page-header/page-header";
import { Trans, useTranslation } from "react-i18next";
import { alpha } from "@mui/material";
import theme from "@styles/theme";
import { Menu } from "@components_v2/menu";
import { styled } from "@mui/system";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCollection, updateCollection, updateCollectionData } from "@modules/collection";
import * as CollectionActions from "@modules/collection";
import menuService from "@services/menuService";
import LinkedAssetsDetailCollection from "@routes/collections/ui/linked-assets-detail-collection";
import PanelDetailsView from "@components/view/panel-details-view/panel-details-view";
import convertToByte from "@cores/convertToByte";
import EditLinkedCollectionTable from "@components/view/panel-details-view/edit-linked-collection-table";
import CollectionColumn from "@components/view/panel-details-view/panel-table-columns-collections";
import EditName from "@components_v2/edit-name/edit-name";
import EditSubmitButtonGroup from "@components_v2/edit-submit-button-group/edit-submit-button-group";
import validateInput, { convertValidationMessage } from "@cores/validateInput";
import { LIMIT_CHAR } from "@constants";
import { setNotification } from "@modules/notification";
import DialogDelete from "@features/dialog/dialog-delete";
import { deleteCollectionAPI } from "@modules/apis/collection";
import { Tabs } from "@components_v2/tabs/tabs";
import { SplitTwoTierGridRight } from "@components_v2/grid/split-two-tier-grid-right";
import useFoldActionButtons from "@hooks/useFoldActionButtons";
import CommentService from "@features/comments/comment-service";
import queryParamsParse from "../../components/params/queryParamsParse";
import { FOLDER_VIEW_DOMAINS } from "@constants";
import Tooltip from "@components_v2/Tooltip/tooltip";
import DialogArchive from "@features/dialog/dialog-archive";
import ReactDOM from "react-dom";
import Loadable from "@react-loadable/revised";
import useArchive from "@hooks/useArchive";
import useDownloadJobs from "@hooks/useDownloadJobs";
import { listJobs } from "@modules/bulkJobs";
import useCheckPermission from "@hooks/useCheckPermission";
import AccessDenied from "@routes/errors/Error403";
import useSettings from "@hooks/useSettings";
import BreadcrumbV2 from "@components_v2/breadcrumb/breadcrumb-v2";
import useCustomMenus from "@hooks/useCustomMenus";
import { siteMode } from "@cores/siteMode";
import PanelLibraryOverview from "@components/view/panel-library-overview/panel-library-overview";

const DetailCollectionContainer = styled(Grid)(({ theme }) => ({
    width: "100%",
    height: "100%",
    overflow: `hidden`,
    flexWrap: `nowrap`,
    "& #detail-collection-assets-view": {
        flex: 1,
        padding: "0 24px 0 0",
        overflow: `hidden`,
        display: `flex`,
        flexDirection: `column`,
        flexWrap: `nowrap`,
        ".detail-collections-table-list": {
            flex: 1,
            overflow: `hidden`,
            border: `none`,
            "> .MuiList-root": {
                position: `relative`,
                bottom: -4,
                maxHeight: `100%`,
                overflow: `auto`,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 4,
            },
        },
    },
    [theme.breakpoints.down("xl")]: {
        "& #detail-collection-assets-view": {
            paddingRight: 12,
            ".detail-collections-table-list": {
                maxHeight: "700px",
                overflow: "overlay",
                "> .MuiList-root": {
                    bottom: 0,
                },
            },
        },
        "& #detail-collection-panel-view": {
            ".MuiTabs-root": {
                marginLeft: 0,
            },
        },
    },
}));

const LoadableDownloadLinkModal = Loadable({
    loader: () => import("@components/modals/DownloadLinkModal"),
    loading: () => null,
});

const DetailCollection = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const params = useParams();
    const history = useHistory();
    const { pathname } = useLocation();
    const { domainStatus, settingsPending } = useSettings({ domain: "collections", type: "DomainStatus" });
    const { requestArchive } = useArchive();
    const { requestDownloadJob } = useDownloadJobs();
    const { getSettingData: getSettingMenuName, resourceSettings: settingsMenuName } = useSettings({
        type: "MenuName",
        domain: "assets",
    });
    const { getCustomMenuBreadcrumb } = useCustomMenus();
    const isCms = siteMode.get() === "CMS";

    const { spaceId, projectId } = menuService.parse(pathname);
    const COLLECTIONS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });
    const COMMENTS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Comments" });

    const { threadId, domain } = queryParamsParse(location.search);
    const isLinkFromEmail = !!threadId;

    const { toggle: foldActionToggle, button: foldActionButton } = useFoldActionButtons({
        type: "comment",
        defaultValue: !settingsPending?.projectSettingData
            ? COMMENTS_PERMISSIONS.READ && isLinkFromEmail && domain === "COLLECTION"
            : undefined,
        hidden: domainStatus.COMMENTS_OFF,
    });

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const collection = useSelector(({ collection }) => collection);
    const customMenus = useSelector(({ customMenus }) => customMenus);

    const [editName, setEditName] = useState("");
    const [editNameActive, setEditNameActive] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [actionPending, setActionPending] = useState({});
    const [modal, setModal] = useState(false);

    const isCustomMenu = useMemo(() => {
        return (
            settingsMenuName?.settings?.MenuName?.selectMenu &&
            settingsMenuName?.settings?.MenuName?.selectMenu?.value === "custom"
        );
    }, [settingsMenuName?.settings?.MenuName?.selectMenu]);

    const isFromListMenu = useMemo(() => {
        if (!customMenus?.data) return false;

        const menuId = pathname.split("/customMenuSets/")?.[1]?.split("/")?.[1];
        if (!menuId) return false;

        for (const menu of customMenus.data) {
            if (menu.id === menuId) {
                return menu.type !== "DETAIL";
            }

            if (menu.subMenus) {
                for (const subMenu of menu.subMenus) {
                    if (subMenu.id === menuId) {
                        return subMenu.type !== "DETAIL";
                    }
                }
            }
        }

        return false;
    }, [customMenus, pathname]);

    const panelData = useMemo(() => {
        const data = collection.data;
        if (!data) return [];

        if (isCms) {
            return [
                {
                    attributions: [
                        { type: "text", subtitle: "Collection ID", value: data.id, isEditable: false },
                        { type: "text", subtitle: "Size", value: convertToByte(data.totalSize), isEditable: false },
                        {
                            type: "date",
                            subtitle: "Created",
                            value: data.created,
                            isEditable: false,
                        },
                    ],
                },
                {
                    attributions: [
                        {
                            type: "textarea",
                            subtitle: "Description",
                            value: data.description,
                            isEditable: true,
                            isEditButtonDisabled: !COLLECTIONS_PERMISSIONS.EDIT,
                        },
                        {
                            id: "tags",
                            type: "tags",
                            subtitle: "Tags",
                            value: data.tags,
                        },
                    ],
                },
            ];
        }

        return [
            {
                title: "Collection Details",
                attributions: [
                    { type: "text", subtitle: "Collection ID", value: data.id, isEditable: false },
                    { type: "text", subtitle: "Size", value: convertToByte(data.totalSize), isEditable: false },
                    {
                        type: "folder",
                        subtitle: "Location",
                        value: { virtualDrive: data.virtualDrive, virtualFolder: data.virtualFolder },
                        isEditable: true,
                        isEditButtonDisabled: !COLLECTIONS_PERMISSIONS.EDIT,
                        onSubmitEditValue: async ({ editValue }) => {
                            dispatch(
                                updateCollection({
                                    id: data.id,
                                    params: { virtualFolder: { id: editValue.folderId } },
                                }),
                            );
                        },
                    },
                    {
                        type: "date",
                        subtitle: "Created",
                        value: data.created,
                        isEditable: false,
                    },
                    {
                        type: "date",
                        subtitle: "Updated",
                        value: data.updated,
                        isEditable: false,
                    },
                ],
            },
            {
                title: "Metadata",
                attributions: [
                    {
                        type: "textarea",
                        subtitle: "Description",
                        value: data.description,
                        isEditable: true,
                        isEditButtonDisabled: !COLLECTIONS_PERMISSIONS.EDIT,
                    },
                    {
                        type: "tags",
                        subtitle: "Tags",
                        value: data.tags,
                        isEditable: true,
                        isEditButtonDisabled: !COLLECTIONS_PERMISSIONS.EDIT,
                    },
                ],
            },
            {
                title: "Linked Collections",
                type: "listView",
                value: {
                    type: "collection",
                    data: data.linkedCollections,
                    component: EditLinkedCollectionTable,
                    columns: CollectionColumn,
                    isEditable: true,
                    isEditButtonDisabled: !COLLECTIONS_PERMISSIONS.EDIT,
                    noDataContent: (
                        <Grid
                            container
                            sx={{
                                height: "60px",
                                p: 1.5,
                                borderRadius: `4px`,
                                border: `1px solid ${theme.palette.secondary[100]}`,
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Typography>
                                {t(`common::msg::No linked collection yet.`, `No linked collection yet.`)}
                            </Typography>
                        </Grid>
                    ),
                },
            },
        ];
    }, [collection.data, isCms, COLLECTIONS_PERMISSIONS.EDIT, t, dispatch]);

    const onHandleChangeEditName = (e) => {
        e.stopPropagation();
        setEditName(e.target.value);
        setValidationErrors({ name: validateInput(e.target.value, { required: true, maxLength: LIMIT_CHAR.name }) });
    };

    const onHandleClickEditName = async () => {
        try {
            setActionPending({ ...actionPending, editName: true });
            await dispatch(updateCollection({ id: params.id, params: { name: editName } }));
            setEditNameActive(false);
        } catch (error) {
            console.log(error);
            dispatch(
                setNotification({
                    type: "error",
                    contents: t(`common::msg::An error occured while changing collection name`),
                }),
            );
        } finally {
            setActionPending({ ...actionPending, editName: undefined });
        }
    };

    const onToggleModal = (key, value) => {
        if (value) {
            setModal(key);
        } else {
            setModal("");
        }
    };

    const onHandleDeleteCollections = async () => {
        try {
            setActionPending({ ...actionPending, delete: true });

            await deleteCollectionAPI(stage.endpoint, project.id, collection.data.id);

            //성공 메세지
            dispatch(
                setNotification({
                    type: "success",
                    contents: (
                        <Trans
                            i18nKey={`common::msg::Deletion of collection {{item}} has been completed.`}
                            values={{ item: collection.data?.name }}
                        />
                    ),
                }),
            );

            history.push(menuService.paths.assetsCollections(stage.id, project.id));
            onToggleModal();
            setActionPending({ ...actionPending, delete: undefined });
        } catch (error) {
            console.error(error);
            dispatch(
                setNotification({
                    type: "error",
                    contents: t(`common::msg::There was an error deleting collection`),
                }),
            );
        }
    };

    const loadCollection = useCallback(async () => {
        try {
            const id = params.id;
            await dispatch(getCollection({ id }));
        } catch (error) {
            //Note: 이게 필요할까? 404을 보여주는게 맞지 않나
            history.push(menuService.paths.assetsCollections(stage.id, project.id));
        }
    }, [params.id, dispatch, stage.id, project.id]);

    const onHandleClickDownload = useCallback(
        (collection) => {
            const downloadRequestTargets = {
                resourceIds: [collection.id],
                projectId: projectId,
                resourceName: collection?.name,
            };

            requestDownloadJob("COLLECTION", downloadRequestTargets, 1);
        },
        [projectId, requestDownloadJob],
    );

    const onHandleClickArchiveCollection = async (isFolder, option) => {
        try {
            setActionPending({ ...actionPending, archive: true });

            let targets = {};
            const ids = [collection.data.id];
            targets = { ids };
            const data = {
                archiveClass: option,
                targets: {
                    collection: targets,
                },
            };
            const isMulti = false;

            await requestArchive(isMulti, data, async () => {
                dispatch(listJobs());
            });
        } catch (e) {
            console.log(e);
        } finally {
            setActionPending({ ...actionPending, archive: false });
            onToggleModal();
        }
    };

    const onHandleClickBackToList = useCallback(() => {
        let url = isCms
            ? menuService.paths.library.assetsCollections(spaceId, projectId)
            : menuService.paths.assetsCollections(spaceId, projectId);

        if (isCustomMenu) {
            url = `${pathname.slice(0, pathname.lastIndexOf("/"))}`;
        }

        history.push(url);
    }, [isCms, spaceId, projectId, isCustomMenu, history, pathname]);

    const menuList = useMemo(() => {
        if (isCms) return [];

        return [
            {
                label: t(`common::label::Share link`, `Delete`),
                onClick: () => {
                    onToggleModal("shareLink", true);
                },
                clickClose: true,
                hidden: compareVersions(stage.version, VERSION_CM_12197_COLLECTION_SHARE_LINK) < 0,
                disabled: !SHARES_PERMISSIONS.EXTERNAL,
            },
            {
                label: t(`common::label::Download`, `Download`),
                onClick: () => {
                    onHandleClickDownload(collection.data);
                },
                clickClose: true,
                divider: true,
                hidden: compareVersions(stage.version, VERSION_CM_12198_COLLECTION_DOWNLOAD) < 0,
                disabled: !DOWNLOAD_PERMISSIONS.DOWNLOAD,
            },
            {
                label: t(`common::label::Archive`, `Archive`),
                onClick: () => {
                    onToggleModal("archive", true);
                },
                clickClose: true,
                divider: true,
                hidden: compareVersions(stage.version, VERSION_CM_12290_COLLECTION_ARCHIVE) < 0,
                disabled: !COLLECTIONS_PERMISSIONS.ARCHIVE,
            },
            {
                label: t(`common::label::Delete`, `Delete`),
                clickClose: true,
                divider: true,
                onClick: () => {
                    onToggleModal("delete", true);
                },
                disabled: !COLLECTIONS_PERMISSIONS.EDIT,
            },
        ];
    }, [
        isCms,
        t,
        stage.version,
        SHARES_PERMISSIONS.EXTERNAL,
        DOWNLOAD_PERMISSIONS.DOWNLOAD,
        COLLECTIONS_PERMISSIONS.ARCHIVE,
        COLLECTIONS_PERMISSIONS.EDIT,
        onHandleClickDownload,
        collection.data,
    ]);

    useEffect(() => {
        if (!COLLECTIONS_PERMISSIONS.READ) return;

        getSettingMenuName("MenuName");
        loadCollection();
    }, []);

    const currentItem = useMemo(() => {
        if ((isCustomMenu && !isFromListMenu) || !collection.data) return null;

        return {
            name: collection.data.name,
        };
    }, [isCms, isFromListMenu, collection.data]);

    const shouldShowEditName = useMemo(() => {
        return !isCms && (!isCustomMenu || isFromListMenu);
    }, [isCms, isCustomMenu, isFromListMenu]);

    return (
        <VersionedComponent version={VERSION_CM_9766_COLLECTIONS}>
            {modal === "delete" && (
                <DialogDelete
                    items={[collection.data]}
                    contents={
                        <Grid maxWidth={"432px"}>
                            <Typography variant={"body1Regular"} color={theme.palette.secondary[800]}>
                                <Grid item>
                                    <span>
                                        <Trans
                                            i18nKey={`common::msg::"{{name}}" will be deleted forever and you won't be abled to restore it.`}
                                            values={{
                                                name: collection.data?.name,
                                            }}
                                        />
                                    </span>
                                </Grid>
                                <Grid item>
                                    <span>
                                        {t(`common::msg::However, assets included in the collection are not deleted.`)}
                                    </span>
                                </Grid>
                            </Typography>
                        </Grid>
                    }
                    actionPending={actionPending.delete}
                    onClickClose={() => onToggleModal()}
                    onClickSubmit={() => {
                        onHandleDeleteCollections();
                    }}
                />
            )}
            {modal === "archive" && (
                <DialogArchive
                    active={true}
                    onClose={() => onToggleModal()}
                    onSubmit={onHandleClickArchiveCollection}
                    actionPending={actionPending.archive}
                    data={[collection.data]}
                />
            )}
            {modal === "shareLink" &&
                ReactDOM.createPortal(
                    <LoadableDownloadLinkModal
                        domain={"COLLECTION"}
                        selectedItems={[collection.data]}
                        onClickClose={() => onToggleModal()}
                        onSubmit={() => {
                            onToggleModal();
                            // afterMutationWorks();
                        }}
                    />,
                    document.body,
                )}
            {!COLLECTIONS_PERMISSIONS.READ || collection.error ? (
                <AccessDenied />
            ) : (
                <>
                    <PageHeader
                        prev={
                            shouldShowEditName ? (
                                <Tooltip title={t(`common::label::Back to list`, `Back to list`)}>
                                    <IconButton size={`large`} circled onClick={onHandleClickBackToList}>
                                        <IconBack size={24} htmlColor={theme.palette.secondary[800]} />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <></>
                            )
                        }
                        title={
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    minHeight: "25px",
                                    mt: theme.spacing(1),
                                    flex: 1,
                                    overflow: "hidden",
                                    minWidth: 0,
                                }}
                            >
                                {collection.data && (
                                    <>
                                        {shouldShowEditName ? (
                                            <EditName
                                                type={"detail"}
                                                isEditable={COLLECTIONS_PERMISSIONS.EDIT && !isCms}
                                                isEdit={editNameActive}
                                                name={collection.data?.name}
                                                value={editName}
                                                valueLength={editName?.length}
                                                onChange={onHandleChangeEditName}
                                                onEditClick={() => {
                                                    setEditNameActive(true);
                                                    setEditName(collection.data?.name);
                                                }}
                                                error={validationErrors.name?.level}
                                                helperText={
                                                    validationErrors.name
                                                        ? validationErrors.name.message ?? t(convertValidationMessage(validationErrors.name.type, { maxLength: LIMIT_CHAR.name }))
                                                        : null
                                                }
                                                actions={
                                                    <EditSubmitButtonGroup
                                                        submitDisabled={
                                                            editName === collection.data?.name || validationErrors.name || actionPending.editName
                                                        }
                                                        submitPending={actionPending.editName}
                                                        onClickSubmit={onHandleClickEditName}
                                                        onClickClose={() => {
                                                            setEditNameActive(false);
                                                            setValidationErrors({ ...validationErrors, name: null });
                                                        }}
                                                    />
                                                }
                                            />
                                        ) : (
                                            <BreadcrumbV2
                                                breadcrumb={getCustomMenuBreadcrumb(isFromListMenu)}
                                                currentItem={currentItem}
                                            />
                                        )}
                                    </>
                                )}
                            </Box>
                        }
                        breadcrumb={
                            compareVersions(stage.version, VERSION_CM_12923_LIST_SEARCHBAR_FILTER) < 0 && (
                                <BreadcrumbsWithRouterPath />
                            )
                        }
                        actions={
                            <>
                                <Menu
                                    open
                                    menuWidth={160}
                                    endIcon={
                                        <Box
                                            sx={{
                                                width: `24px`,
                                                height: `24px`,
                                                borderRadius: `100%`,
                                                display: `flex`,
                                                alignItems: `center`,
                                                justifyContent: `center`,
                                                marginRight: `-10px`,
                                            }}
                                        >
                                            <IconArrowDropDown size={24} />
                                        </Box>
                                    }
                                    endOpenIcon={
                                        <Box
                                            sx={{
                                                width: `24px`,
                                                height: `24px`,
                                                borderRadius: `100%`,
                                                display: `flex`,
                                                alignItems: `center`,
                                                justifyContent: `center`,
                                                marginRight: `-10px`,
                                                backgroundColor: alpha(theme.palette.secondary.light, 0.08),
                                            }}
                                        >
                                            <IconArrowDropUp size={20} />
                                        </Box>
                                    }
                                    label={t("common::label::Actions")}
                                    size={"medium"}
                                    variant={"outlined"}
                                    color={"greySecondary"}
                                    placement={"right"}
                                    list={menuList}
                                />
                            </>
                        }
                    />
                    <Divider flexItem />
                    <DetailCollectionContainer container>
                        <SplitTwoTierGridRight
                            orientation={"vertical"}
                            gridLeft={
                                <LinkedAssetsDetailCollection collectionId={params.id} isCustomMenu={isCustomMenu} />
                            }
                            gridRight={
                                isCms ? (
                                    <Grid container flexDirection={"column"} sx={{ height: "100%" }}>
                                        <Grid container flexDirection={"column"} flex={1} hidden={!!foldActionToggle}>
                                            <PanelLibraryOverview data={panelData} />
                                        </Grid>
                                        {!domainStatus.COMMENTS_OFF && (
                                            <CommentService
                                                domain={"COLLECTION"}
                                                resourceId={params.id}
                                                isShowReplyView={foldActionToggle}
                                                isCustomMenu={isCustomMenu}
                                                isCms={isCms}
                                                projectId={projectId}
                                            />
                                        )}
                                    </Grid>
                                ) : (
                                    <Grid
                                        container
                                        id={"detail-collection-panel-view"}
                                        flexDirection={"column"}
                                        height={`100%`}
                                    >
                                        <Grid
                                            container
                                            flexDirection={"column"}
                                            height={`100%`}
                                            hidden={!!foldActionToggle}
                                        >
                                            <Grid item>
                                                <Tabs
                                                    indicatorColor={"secondary"}
                                                    indicatorPosition={"top"}
                                                    textColor={"secondary"}
                                                    isBordered={true}
                                                    value={0}
                                                    onChangeTab={() => {}}
                                                    items={[
                                                        {
                                                            label: t(`common::label::Details`, `Details`),
                                                            value: 0,
                                                        },
                                                    ]}
                                                    sx={{ marginLeft: "0 !important" }}
                                                />
                                            </Grid>
                                            <Divider sx={{ mt: `-1px` }} />
                                            <Grid item xs sx={{ overflow: `auto` }} p={0.5}>
                                                <PanelDetailsView
                                                    id={collection.data?.id}
                                                    domain={FOLDER_VIEW_DOMAINS.COLLECTIONS}
                                                    data={panelData}
                                                    actions={CollectionActions}
                                                    afterMutation={(params) =>
                                                        dispatch(
                                                            updateCollectionData({ ...collection.data, ...params }),
                                                        )
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                        {!domainStatus.COMMENTS_OFF && (
                                            <CommentService
                                                domain={"COLLECTION"}
                                                resourceId={params.id}
                                                isShowReplyView={foldActionToggle}
                                                isCustomMenu={isCustomMenu}
                                                isCms={isCms}
                                                projectId={projectId}
                                            />
                                        )}
                                    </Grid>
                                )
                            }
                            gridStyle={{ left: { marginTop: theme.spacing(2.5) } }}
                            foldActionButtons={foldActionButton}
                            expandButtonToggle={foldActionToggle}
                            initialIsFolded={isCms}
                            gridRightProps={{ minSize: 280, maxSize: 448 }}
                        />
                    </DetailCollectionContainer>
                </>
            )}
        </VersionedComponent>
    );
};

export default DetailCollection;
